import React, { useMemo } from 'react'

import {
  F0Dialog,
  F0Form,
  f0FormField,
  useF0Form,
  useF0FormDefinition,
} from '@factorialco/f0-react'
import type { RecordType } from '@factorialco/f0-react'
import { PhoneNumberUtil } from 'google-libphonenumber'
import {
  CreateVendorFromDropdownDocument,
  PurchaseRequestVendorSelectorDocument,
} from 'generated/resources/graphql'
import { z } from 'zod'

import { countries } from 'modules/core/lib/countryList'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

const phoneUtil = PhoneNumberUtil.getInstance()

interface CreateVendorFromDropdownModalProps {
  companyId: string
  initialName: string
  onClose: () => void
  onCreated: (vendor: RecordType) => void
}

export const CreateVendorFromDropdownModal: React.FC<CreateVendorFromDropdownModalProps> = ({
  companyId,
  initialName,
  onClose,
  onCreated,
}) => {
  const [createVendor] = useMutation(CreateVendorFromDropdownDocument, {
    extractorFn: (data) => data.financeMutations.createFinanceContact,
    refetchQueries: [PurchaseRequestVendorSelectorDocument],
    awaitRefetchQueries: true,
  })

  const { formRef, submit, isSubmitting, hasErrors } = useF0Form()

  const countryOptions = useMemo(
    () => countries(i18n.locale).map((c) => ({ value: c.value.toUpperCase(), label: c.label })),
    [i18n.locale]
  )

  const schema = z.object({
    name: f0FormField(z.string().trim().min(1), {
      label: i18n.t('finance.contacts.contact_form.fields.name'),
    }),
    countryCode: f0FormField(z.string().optional(), {
      label: i18n.t('finance.contacts.contact_form.fields.country'),
      fieldType: 'select',
      showSearchBox: true,
      options: countryOptions,
    }),
    taxId: f0FormField(z.string().optional(), {
      label: i18n.t('finance.contacts.contact_form.fields.tax_identification_number'),
    }),
    phoneNumber: f0FormField(
      z
        .string()
        .refine(
          (val) => {
            if (!val) return true
            try {
              return phoneUtil.isValidNumber(phoneUtil.parse(val, 'ZZ'))
            } catch {
              return false
            }
          },
          { message: i18n.t('finance.contacts.contact_form.fields.phone_number_invalid') }
        )
        .optional(),
      {
        label: i18n.t('finance.contacts.contact_form.fields.phone_number'),
      }
    ),
    website: f0FormField(
      z.preprocess((val) => (val === '' ? undefined : val), z.string().url().optional()),
      {
        label: i18n.t('finance.contacts.contact_form.fields.website'),
      }
    ),
  })

  const formDefinition = useF0FormDefinition({
    name: 'create-vendor-from-dropdown',
    schema,
    defaultValues: {
      name: initialName,
      countryCode: undefined,
      taxId: '',
      phoneNumber: '',
      website: '',
    },
    onSubmit: async ({ data }) => {
      const result = await createVendor({
        variables: {
          companyId,
          name: data.name,
          address: data.countryCode
            ? { countryCode: data.countryCode, line1: '', city: '', postalCode: '', state: '' }
            : undefined,
          phoneNumber: data.phoneNumber || undefined,
          website: data.website || undefined,
          taxId: data.taxId || undefined,
        },
      })

      let fieldErrors: Record<string, string> = {}
      const handled = handleResult(result, (errors) => {
        fieldErrors = errors
      })

      if (!handled.success) {
        return { success: false, errors: fieldErrors }
      }

      const contact = handled.data?.contact
      if (contact) {
        onCreated({ id: contact.id, name: contact.name, legalName: contact.legalName })
      }

      return { success: true }
    },
    submitConfig: { type: 'default', hideSubmitButton: true },
  })

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      title={i18n.t('finance.contacts.contacts_list.new_vendor')}
      primaryAction={{
        label: i18n.t('common.submit'),
        onClick: submit,
        loading: isSubmitting,
        disabled: hasErrors,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
      container={null}
    >
      <F0Form formRef={formRef} formDefinition={formDefinition} />
    </F0Dialog>
  )
}
